import { FC, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Button,
  Icon,
  Spinner,
} from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { ConversationAreaProps } from './shared/types';
import { MessageBubble } from './components/MessageBubble';
import { ReplyBox } from './components/ReplyBox';

export const ConversationArea: FC<ConversationAreaProps> = ({
  caseInfo,
  messages,
  unhandledCount,
  onResolveAllUnhandled,
  onSendMessage,
  applicantInfo,
  isLoading,
  error,
}) => {
  const L = useLocalizedStrings();
  const [resolvingAll, setResolvingAll] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Delay showing the spinner to avoid flashing for fast loads
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(true);
      }, 300); // Show spinner after 300ms
      return () => clearTimeout(timer);
    } else {
      setShowSpinner(false);
    }
  }, [isLoading]);

  // Scroll to bottom when messages load or change
  // useLayoutEffect runs before browser paint, preventing visual flicker
  useLayoutEffect(() => {
    if (messagesContainerRef.current && messages.length > 0 && !isLoading) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // Only auto-scroll if user is already near the bottom or on initial load
      if (isNearBottom || container.scrollTop === 0) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  // Build "Via [channels]" text based on unique message types
  const channelText = useMemo(() => {
    const uniqueKinds = new Set(messages.map(msg => msg.kind));
    const channels: string[] = [];

    if (uniqueKinds.has('email')) channels.push(L.applicant.comms.email);
    if (uniqueKinds.has('sms') || uniqueKinds.has('mms')) channels.push(L.applicant.comms.sms);
    if (uniqueKinds.has('call')) channels.push(L.applicant.comms.call);

    if (channels.length === 0) return '';

    // Join with "and" for the last item
    if (channels.length === 1) {
      return `${L.applicant.comms.via} ${channels[0]}`;
    } else if (channels.length === 2) {
      return `${L.applicant.comms.via} ${channels[0]} ${L.applicant.comms.and} ${channels[1]}`;
    } else {
      const lastChannel = channels.pop();
      return `${L.applicant.comms.via} ${channels.join(', ')}, ${L.applicant.comms.and} ${lastChannel}`;
    }
  }, [messages, L.applicant.comms]);

  const handleResolveAll = async () => {
    setResolvingAll(true);
    try {
      await onResolveAllUnhandled();
    } finally {
      setResolvingAll(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Conversation Header */}
      <div className="bg-white border-b p-3">
        <div className="flex items-stretch justify-between gap-3">
          <div className="flex flex-col justify-center">
            <div className="text-md font-semibold">
              {L.support.conversation_with.replace('{}', applicantInfo?.['legal_name'] || caseInfo.contact)}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {channelText}
            </div>
          </div>
          <Button
            variant="secondary"
            className="self-stretch"
            onClick={handleResolveAll}
            disabled={unhandledCount === 0 || resolvingAll}
          >
            {L.support.mark_all_unhandled_as_handled}
          </Button>
        </div>
      </div>

      {/* Yellow Reminder Banner */}
      {!bannerDismissed && (
        <div className="bg-yellow-400/5 p-3 border-b border-gray-200">
          <div className="bg-yellow-500/10 border-l-4 border-yellow-400 rounded-lg p-3">
            <div className="flex justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-900 font-semibold mb-2">
                  {L.applicant.comms.reminder_staff}
                </p>
                <p className="text-sm text-gray-900 mb-2">
                  {L.applicant.comms.important_obtain_explicit_consent}
                </p>
                <p className="text-sm text-gray-900 font-semibold mb-1">
                  {L.applicant.comms.example_consent}
                </p>
                <p className="text-sm text-gray-500 italic mb-0">
                  {L.applicant.comms.before_we_begin}
                </p>
              </div>
              <Button
                variant="unstyled"
                onClick={() => setBannerDismissed(true)}
                icon={<Icon name="XMarkIcon" className="h-5 w-5" />}
                iconPlacement="none"
                className="text-gray-600 hover:text-gray-900 flex-shrink-0 self-start"
                aria-label={L.component_library.alert.dismiss}
              />
            </div>
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-white">
        {isLoading ? (
          showSpinner ? <Spinner /> : null
        ) : error ? (
          <div className="text-center text-red-500 mt-8">
            {L.applicant.comms.error_loading_messages}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            {L.applicant.comms.no_messages_have_been_sent_or_received}
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isLast={index === messages.length - 1}
                applicantInfo={applicantInfo}
              />
            ))}
          </div>
        )}
      </div>

      {/* Reply Box */}
      <ReplyBox onSendMessage={onSendMessage} applicantInfo={applicantInfo} messages={messages} />
    </div>
  );
};
